import { useEffect, useContext, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import { appLogger, Http } from "../../../src/utils";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import { ChatbotLLMOptions, ROLE_USER_SUPERADMIN } from "../../model";

const features = [
  {
    category: "Integration",
    options: [
      { label: "Jira", name: "enableJira" },
      { label: "Zendesk", name: "enableZendesk" },
      { label: "Google Playstore", name: "enablePlaystore" },
      { label: "Apple Appstore", name: "enableAppstore" },
      { label: "Emails", name: "enableMailReply" },
    ],
  },
  {
    category: "Dashboard",
    options: [
      { label: "Correlation", name: "enableCorrelation" },
      { label: "Analytics", name: "enableAnalytics" },
    ],
  },
  {
    category: "Agent",
    options: [
      { label: "Feedback management", name: "enableFeedback" },
      { label: "Ticketing system", name: "enableBugReport" },
      { label: "Feature requests", name: "enableFeatureRequest" },
      { label: "Chatbot", name: "enableChatbot" },
      { label: "Dynamic feedback", name: "enableDynamicFeedback" },
    ],
  },
];

const logLevelInitialValues = [
  {
    label: "Trace",
    value: "trace",
  },
  {
    label: "Debug",
    value: "debug",
  },
  {
    label: "Info",
    value: "info",
  },
  {
    label: "Warn",
    value: "warn",
  },
  {
    label: "Error",
    value: "error",
  },
  {
    label: "Fatal",
    value: "fatal",
  },
];

const chatbotLLMInitialValues: { label: string; value: ChatbotLLMOptions }[] = [
  {
    label: "GPT",
    value: "GPT",
  },
  {
    label: "LAMA",
    value: "LAMA",
  },
];

const initialFormValues = {
  enableJira: false,
  enableZendesk: false,
  enableMailReply: false,
  enableCorrelation: false,
  enableAnalytics: false,
  enablePlaystore: false,
  enableAppstore: false,
  enableFeedback: false,
  enableBugReport: false,
  enableFeatureRequest: false,
  enableChatbot: false,
  enableDynamicFeedback: false,
};

const Features = (props: any) => {
  const { userInfo, setEnabledFeatures } = useContext(
    UserContext
  ) as UserLoginInfo;
  //orgId
  const orgId = localStorage.getItem("userOrgId");

  const [formValues, setFormValues] = useState(initialFormValues);
  const [logLevel, setLogLevel] = useState<string>("");
  const [chatbotLLM, setChatbotLLM] = useState<ChatbotLLMOptions | "">("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogLevelChange = (e: any) => {
    const { value } = e.target;
    setLogLevel(value);
  };

  const handleChatbotLLMChange = (e: any) => {
    const { value } = e.target;
    setChatbotLLM(value);
  };

  const handleFeatureChange = (name: string) => {
    if (name === "enableChatbot") {
      if (formValues[name as keyof typeof formValues]) {
        setChatbotLLM("");
      }
    }
    setFormValues({
      ...formValues,
      [name]: !formValues[name as keyof typeof formValues],
    });
  };

  const handleFetchEnabledFeatures = async (id: string) => {
    setIsLoading(true);
    try {
      const res: any = await Http.get({
        url: `/api/v2/enabledFeatures/${id}`,
      });

      if (
        res?.enabledFeatures &&
        Object.keys(res?.enabledFeatures).length > 0
      ) {
        const { enabledFeatures } = res;
        localStorage.setItem(
          "userenabledFeatures",
          JSON.stringify(enabledFeatures)
        );

        setFormValues(enabledFeatures);
      }
      if (res.logLevel) {
        setLogLevel(res.logLevel);
      }
      if (res.chatbotLLM) {
        setChatbotLLM(res.chatbotLLM);
      }
      setIsLoading(false);
    } catch (err) {
      appLogger.error({
        errObj: {
          method: "fetchGroup",
          component: "add-product",
          fileName: "basicDetails.tsx",
          err: err,
        },
      });
      props.snackbarShowMessage(
        "Failed fetching enabled features",
        "error",
        3000
      );
      setIsLoading(false);
    }
  };

  const handleSaveEnabledFeatures = async () => {
    try {
      const data = { enabledFeatures: formValues, logLevel, chatbotLLM };

      await Http.post({
        url: `/api/v2/enabledFeatures/${orgId}`,
        body: data,
        state: userInfo,
      });

      setEnabledFeatures(data.enabledFeatures);

      props.snackbarShowMessage("Features saved successfully", "success", 3000);
    } catch (err) {
      appLogger.error({
        errObj: {
          method: "fetchGroup",
          component: "add-product",
          fileName: "basicDetails.tsx",
          err: err,
        },
      });

      props.snackbarShowMessage(
        "Failed saving enabled features",
        "error",
        3000
      );
    }
  };

  useEffect(() => {
    if (orgId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleFetchEnabledFeatures(orgId);
    }
  }, [orgId]);

  return (
    <Grid container className={isLoading ? "loader" : ""}>
      <Grid item xs={12}>
        <Box className="table-wrap">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography>Feature</Typography>
                </TableCell>
                <TableCell>
                  <Typography>Active</Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {features.map((feature) => {
                return (
                  <>
                    <TableRow
                      key={feature.category}
                      sx={{ borderBottom: "1px solid #ccc" }}
                    >
                      <TableCell sx={{ borderRight: "1px solid #ccc" }}>
                        {feature.category}
                      </TableCell>
                      {feature.options?.map((data: any) => (
                        <p style={{ margin: "0px" }}>
                          <TableCell>
                            <Checkbox
                              // disabled={
                              //   userInfo &&
                              //   userInfo.userDetails &&
                              //   userInfo.userDetails.userRole ===
                              //     ROLE_USER_SUPERADMIN
                              //     ? false
                              //     : true
                              // }
                              checked={
                                formValues[data.name as keyof typeof formValues]
                              }
                              disabled
                              onClick={() => handleFeatureChange(data.name)}
                            />
                            {data.label}
                          </TableCell>
                        </p>
                      ))}
                    </TableRow>
                  </>
                );
              })}

              <TableRow>
                <TableCell
                  sx={{ p: "20px !important", borderRight: "1px solid #ccc" }}
                >
                  Log level
                </TableCell>
                <TableCell>
                  <FormControl
                    className="custom-select"
                    sx={{ width: "300px" }}
                  >
                    <InputLabel>Log level</InputLabel>
                    <Select
                      value={logLevel}
                      label="Log level"
                      onChange={handleLogLevelChange}
                      // disabled={
                      //   userInfo &&
                      //   userInfo.userDetails &&
                      //   userInfo.userDetails.userRole === ROLE_USER_SUPERADMIN
                      //     ? false
                      //     : true
                      // }
                      disabled
                    >
                      {logLevelInitialValues.map((level) => {
                        return (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            </TableBody>

            <TableFooter>
              <TableRow></TableRow>
            </TableFooter>
          </Table>
        </Box>
      </Grid>
    </Grid>
  );
};

export default withSnackbar(Features);
